import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  useMediaQuery, useTheme,
} from '@mui/material';
import type { Opportunity } from '../../api/types';
import { useMyOpenOpportunities } from '../../api/hooks';
import { useCurrentUser } from '../../auth/useCurrentUser';
import { ActionFormDialog } from './ActionFormDialog';

/** My Actions has no record in context, so the opportunity is chosen first. The picker is scoped
 *  to the caller's own open deals (ADR-75) — on a page called "My actions", offering every deal in
 *  the system invites logging against someone else's. */
export function MyActionDialog({
  open, onClose, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useCurrentUser();

  const [selected, setSelected] = useState<Opportunity | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const { data } = useMyOpenOpportunities(user?.id, open);
  const options = data?.items ?? [];

  const closeAll = () => {
    setFormOpen(false);
    setSelected(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open && !formOpen} onClose={closeAll} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>{t('myActions.whichOpportunity')}</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ pt: 1 }}
            options={options}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={(option) => `${option.title} — ${option.customerName}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText={t('myActions.noOwnedOpportunities')}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('myActions.opportunity')}
                placeholder={t('common.startTyping')}
                inputProps={{ ...params.inputProps, 'data-testid': 'my-action-opportunity-input' }}
              />
            )}
            data-testid="my-action-opportunity"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAll}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!selected}
            onClick={() => setFormOpen(true)}
            data-testid="my-action-continue"
          >
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {selected && (
        <ActionFormDialog
          open={formOpen}
          customerId={selected.customerId}
          opportunityId={selected.id}
          onClose={closeAll}
          onSaved={onSaved}
        />
      )}
    </>
  );
}
